import React, { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Calendar, X, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";

// Phase 25.3b — Public demo booking widget.
// Floating "BOOK A DEMO" button + modal form. Creates a sales_pipeline row
// at DEMO_BOOKED stage, fires HOT-style alert to admin.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TIME_SUGGESTIONS = [
  "This week — anytime",
  "Next week — anytime",
  "Tuesday morning (AEDT)",
  "Wednesday afternoon (AEDT)",
  "Thursday morning (AEDT)",
  "Flexible — propose a time",
];
const EMPLOYEE_BANDS = ["1-10", "11-50", "51-200", "200+"];

export default function BookDemoButton({ source = "landing_page", variant = "floating" }) {
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", company: "", role: "",
    industry: "", employee_count: "", preferred_time: "", use_case: "",
  });

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name || !form.email || !form.company) {
      toast.error("Name, email, and company are required");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/book-demo`, { ...form, source });
      setSubmitted(true);
      toast.success("Demo booked — we'll be in touch within 24h.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Demo booking failed");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => { setSubmitted(false); setForm({ name: "", email: "", company: "", role: "", industry: "", employee_count: "", preferred_time: "", use_case: "" }); }, 300);
  };

  return (
    <>
      {variant === "floating" && (
        <button
          onClick={() => setOpen(true)}
          data-testid="book-demo-floating-btn"
          className="fixed bottom-6 right-6 z-40 bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 shadow-2xl hover:opacity-90 inline-flex items-center gap-2 group"
        >
          <Calendar className="w-3.5 h-3.5" />
          BOOK A DEMO
          <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
        </button>
      )}
      {variant === "inline" && (
        <button
          onClick={() => setOpen(true)}
          data-testid="book-demo-inline-btn"
          className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 inline-flex items-center gap-2 group"
        >
          <Calendar className="w-3.5 h-3.5" />
          BOOK A DEMO
          <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
        </button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
          onClick={close}
          data-testid="book-demo-modal"
        >
          <div
            className="w-full max-w-xl bg-black border border-zinc-800 shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="border-b border-zinc-900 px-5 py-4 flex items-center justify-between sticky top-0 bg-black/95 backdrop-blur">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFB000]">// BOOK A LIVE DEMO</div>
                <div className="font-heading font-black text-xl tracking-tight mt-1">30 min · with the founder</div>
              </div>
              <button onClick={close} className="text-zinc-500 hover:text-white" data-testid="book-demo-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            {submitted ? (
              <div className="p-8 text-center">
                <CheckCircle2 className="w-12 h-12 text-[#00E676] mx-auto mb-4" />
                <h3 className="font-heading font-black text-2xl tracking-tight">Demo booked.</h3>
                <p className="text-sm text-zinc-400 mt-2 max-w-sm mx-auto leading-relaxed">
                  We've added you to our calendar queue. You'll hear from us within 24 hours
                  at <strong className="text-white">{form.email}</strong> with a confirmation
                  and calendar invite.
                </p>
                <button
                  onClick={close}
                  data-testid="book-demo-done"
                  className="mt-6 border border-zinc-800 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-zinc-300 hover:text-white"
                >
                  CLOSE
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="p-5 space-y-3">
                <p className="text-xs text-zinc-500 leading-relaxed mb-2">
                  Bloomberg-style terminal for your business. Bring real data — DARA will
                  ingest it live during the demo and you'll see the output the same day.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input label="YOUR NAME *" value={form.name} onChange={v => setForm({ ...form, name: v })} testid="demo-name" />
                  <Input label="ROLE / TITLE" value={form.role} onChange={v => setForm({ ...form, role: v })} placeholder="Founder · COO · CFO" testid="demo-role" />
                  <Input label="EMAIL *" type="email" value={form.email} onChange={v => setForm({ ...form, email: v })} testid="demo-email" />
                  <Input label="COMPANY *" value={form.company} onChange={v => setForm({ ...form, company: v })} testid="demo-company" />
                  <Input label="INDUSTRY" value={form.industry} onChange={v => setForm({ ...form, industry: v })} placeholder="Hospitality · Mining · Retail" testid="demo-industry" />
                  <Select label="EMPLOYEE COUNT" value={form.employee_count} onChange={v => setForm({ ...form, employee_count: v })} options={["", ...EMPLOYEE_BANDS]} testid="demo-employees" />
                </div>
                <Select label="PREFERRED TIME (FLEXIBLE)" value={form.preferred_time} onChange={v => setForm({ ...form, preferred_time: v })} options={["", ...TIME_SUGGESTIONS]} testid="demo-time" />
                <Textarea label="WHAT WOULD YOU MOST WANT TO SEE? (OPTIONAL)" rows={3} value={form.use_case} onChange={v => setForm({ ...form, use_case: v })}
                  placeholder="e.g. How DARA forecasts our daily POS revenue · Whether DARA Briefings beat our current reporting · Industrial pilot fit"
                  testid="demo-use-case" />
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="demo-submit-btn"
                  className="w-full bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-4 py-3 hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Calendar className="w-3.5 h-3.5" />}
                  {busy ? "BOOKING…" : "BOOK MY DEMO →"}
                </button>
                <div className="font-mono text-[9px] text-zinc-700 text-center mt-2">
                  No spam. Reply STOP anytime. We'll only email about your demo.
                  {" · "}<a href="/security" target="_blank" rel="noreferrer" className="text-[#FFB000] hover:underline">Your data, encrypted &amp; never trained on</a>.
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function Input({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 block mb-1">{label}</span>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
      />
    </label>
  );
}
function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 block mb-1">{label}</span>
      <select value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]">
        {options.map(o => (<option key={o} value={o}>{o}</option>))}
      </select>
    </label>
  );
}
function Textarea({ label, value, onChange, rows = 3, placeholder, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 block mb-1">{label}</span>
      <textarea value={value || ""} onChange={e => onChange(e.target.value)} rows={rows} placeholder={placeholder} data-testid={testid}
        className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000] resize-none" />
    </label>
  );
}
